import React, { useRef, useEffect, useCallback, memo } from 'react';
import type { ExecutionStep, StringStatePayload, ArrayStatePayload, StringMetadata } from '../../../core/types/rust-engine-interfaces';
import './roundRectPolyfill';

interface StringVisualizerProps {
  currentStep: ExecutionStep | null;
  algorithmId?: string;
}

type StringSignature = 'kmp' | 'boyer-moore' | 'rabin-karp' | 'z-algo' | 'manacher' | 'aho-corasick' | 'naive' | 'rolling-hash' | 'kasai' | 'suffix-array' | 'suffix-automaton' | 'ukkonen' | 'none';

function detectStringSignature(algorithmId: string): StringSignature {
  const id = algorithmId.toLowerCase();
  if (id.includes('kmp')) return 'kmp';
  if (id.includes('boyer')) return 'boyer-moore';
  if (id.includes('rabin')) return 'rabin-karp';
  if (id.includes('z-algo') || id.includes('z-function') || id.includes('z-algorithm')) return 'z-algo';
  if (id.includes('manacher')) return 'manacher';
  if (id.includes('aho')) return 'aho-corasick';
  if (id.includes('naive') || id.includes('brute')) return 'naive';
  if (id.includes('rolling')) return 'rolling-hash';
  if (id.includes('kasai')) return 'kasai';
  if (id.includes('suffix-array')) return 'suffix-array';
  if (id.includes('suffix-automaton')) return 'suffix-automaton';
  if (id.includes('ukkonen')) return 'ukkonen';
  return 'none';
}

// ── Layout constants ──────────────────────────────────────────────────────────

const CELL_W    = 40;     // token width  (wider than height for readability)
const CELL_H    = 42;     // token height
const GAP       = 4;
const STRIDE    = CELL_W + GAP;
const PAD_X     = 62;     // left margin (label column)
const PAD_Y_TOP = 36;
const ROW_GAP   = 28;     // vertical space between text & pattern rows
const CORNER    = 6;      // border-radius for token blocks

// ── Color palette ─────────────────────────────────────────────────────────────

const C = {
  cellDefault:    '#0d1e33',
  borderDefault:  '#1e3a52',
  textDefault:    '#94a3b8',

  cellWindow:     '#0c1f3d',
  borderWindow:   '#3b82f6',
  textWindow:     '#93c5fd',
  windowOverlay:  'rgba(59,130,246,0.09)',

  cellScan:       '#2c1800',
  borderScan:     '#f59e0b',
  textScan:       '#fde68a',
  glowScan:       '#f59e0b',

  cellMatch:      '#031a0f',
  borderMatch:    '#22c55e',
  textMatch:      '#4ade80',

  cellMismatch:   '#2a0808',
  borderMismatch: '#ef4444',
  textMismatch:   '#f87171',
  glowMismatch:   '#ef4444',

  label:          '#475569',
  index:          '#2d3e50',
  indexActive:    '#f59e0b',
  glow:           '#f59e0b',

  kmpCell:        '#0f1e31',
  kmpBorder:      '#1e3a5f',
  kmpText:        '#60a5fa',
  kmpLabel:       '#3b82f6',

  hashMatch:      '#22c55e',
  hashMiss:       '#ef4444',

  explainBg:      'rgba(9,9,11,0.92)',
  explainText:    '#71717a',
};

// ── Cell state ─────────────────────────────────────────────────────────────────

type CellState = 'default' | 'window' | 'scan' | 'match' | 'mismatch';

interface CellTheme { fill: string; border: string; text: string; glow: string | null; sw: number }

function cellTheme(state: CellState): CellTheme {
  switch (state) {
    case 'match':    return { fill: C.cellMatch,    border: C.borderMatch,    text: C.textMatch,    glow: null,          sw: 1.5 };
    case 'mismatch': return { fill: C.cellMismatch, border: C.borderMismatch, text: C.textMismatch, glow: C.glowMismatch, sw: 2   };
    case 'scan':     return { fill: C.cellScan,     border: C.borderScan,     text: C.textScan,     glow: C.glowScan,     sw: 2   };
    case 'window':   return { fill: C.cellWindow,   border: C.borderWindow,   text: C.textWindow,   glow: null,          sw: 1   };
    default:         return { fill: C.cellDefault,  border: C.borderDefault,  text: C.textDefault,  glow: null,          sw: 1   };
  }
}

// ── Draw one token block ───────────────────────────────────────────────────────

function drawToken(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  char: string,
  displayIdx: number | null,
  state: CellState,
  showPointer: boolean,
) {
  const th = cellTheme(state);

  ctx.save();
  if (th.glow) {
    ctx.shadowColor = th.glow;
    ctx.shadowBlur  = 16;
  }

  ctx.fillStyle = th.fill;
  ctx.beginPath();
  ctx.roundRect(x, y, CELL_W, CELL_H, CORNER);
  ctx.fill();

  ctx.strokeStyle = th.border;
  ctx.lineWidth   = th.sw;
  ctx.beginPath();
  ctx.roundRect(x, y, CELL_W, CELL_H, CORNER);
  ctx.stroke();

  ctx.restore();

  // Sheen on match/scan cells
  if (state === 'match' || state === 'scan') {
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.fillRect(x + 2, y + 2, CELL_W - 4, 4);
  }

  // Character
  ctx.fillStyle    = th.text;
  ctx.font         = 'bold 16px monospace';
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(char || ' ', x + CELL_W / 2, y + CELL_H / 2);

  // Index below token
  if (displayIdx !== null) {
    ctx.fillStyle    = state === 'scan' ? C.indexActive : C.index;
    ctx.font         = '8px monospace';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(String(displayIdx), x + CELL_W / 2, y + CELL_H + 3);
  }

  // Downward filled triangle pointer
  if (showPointer) {
    const pcx = x + CELL_W / 2;
    const pcy = y - 5;
    ctx.fillStyle = C.glow;
    ctx.save();
    ctx.shadowColor = C.glow;
    ctx.shadowBlur  = 8;
    ctx.beginPath();
    ctx.moveTo(pcx - 7, pcy - 10);
    ctx.lineTo(pcx + 7, pcy - 10);
    ctx.lineTo(pcx,     pcy);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
}

// ── Draw sliding window bracket ───────────────────────────────────────────────
// Renders a two-layer visual:
//   1. Translucent blue filled rounded-rect behind the window cells
//   2. Solid blue rounded-rect stroke around the entire window span

function drawWindowBracket(
  ctx: CanvasRenderingContext2D,
  rowX: number,
  rowY: number,
  winStart: number,
  winEnd: number,
) {
  if (winStart < 0 || winEnd < winStart) return;

  const x   = rowX + winStart * STRIDE - 3;
  const y   = rowY - 3;
  const w   = (winEnd - winStart + 1) * STRIDE - GAP + 6;
  const h   = CELL_H + 6;
  const r   = CORNER + 2;

  // Filled overlay
  ctx.fillStyle = C.windowOverlay;
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.fill();

  // Stroke outline
  ctx.strokeStyle = C.borderWindow;
  ctx.lineWidth   = 1.5;
  ctx.globalAlpha = 0.55;
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

// ── Draw labelled character row ────────────────────────────────────────────────

function drawCharRow(
  ctx: CanvasRenderingContext2D,
  label: string,
  chars: string[],
  rowX: number,
  rowY: number,
  scanIdx: number,
  matchedSet: Set<number>,
  mismatchSet: Set<number>,
  windowStart: number,
  windowEnd: number,
  indexOffset: number,
) {
  // Row label
  ctx.fillStyle    = C.label;
  ctx.font         = 'bold 10px sans-serif';
  ctx.textAlign    = 'right';
  ctx.textBaseline = 'middle';
  ctx.fillText(label.toUpperCase(), rowX - 8, rowY + CELL_H / 2);

  // Window bracket first (drawn under tokens)
  if (windowStart >= 0 && windowEnd >= windowStart) {
    drawWindowBracket(ctx, rowX, rowY, windowStart - indexOffset, windowEnd - indexOffset);
  }

  chars.forEach((ch, i) => {
    const globalIdx  = i + indexOffset;
    const x          = rowX + i * STRIDE;
    const isMatch    = matchedSet.has(globalIdx);
    const isMismatch = mismatchSet.has(globalIdx);
    const isScan     = globalIdx === scanIdx;
    const inWindow   = windowStart >= 0 && globalIdx >= windowStart && globalIdx <= windowEnd;

    let state: CellState = 'default';
    if (isMatch)         state = 'match';
    else if (isMismatch) state = 'mismatch';
    else if (isScan)     state = 'scan';
    else if (inWindow)   state = 'window';

    drawToken(ctx, x, rowY, ch, globalIdx, state, isScan);
  });
}

// ── KMP failure-function strip ─────────────────────────────────────────────────

function drawKmpStrip(
  ctx: CanvasRenderingContext2D,
  table: number[],
  chars: string[],
  rowX: number,
  y: number,
) {
  const cellH = 22;
  ctx.fillStyle    = C.kmpLabel;
  ctx.font         = 'bold 9px sans-serif';
  ctx.textAlign    = 'right';
  ctx.textBaseline = 'middle';
  ctx.fillText('π[ ]', rowX - 8, y + cellH / 2);

  table.slice(0, chars.length).forEach((val, i) => {
    const x = rowX + i * STRIDE;

    ctx.fillStyle = C.kmpCell;
    ctx.beginPath();
    ctx.roundRect(x, y, CELL_W, cellH, 3);
    ctx.fill();

    ctx.strokeStyle = C.kmpBorder;
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.roundRect(x, y, CELL_W, cellH, 3);
    ctx.stroke();

    ctx.fillStyle    = C.kmpText;
    ctx.font         = 'bold 10px monospace';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(val), x + CELL_W / 2, y + cellH / 2);
  });
}

// ── Rabin-Karp hash chip ──────────────────────────────────────────────────────

function drawHashChip(
  ctx: CanvasRenderingContext2D,
  windowHash: number | string,
  patternHash: number | string,
  x: number,
  y: number,
) {
  const match = String(windowHash) === String(patternHash);
  const color = match ? C.hashMatch : C.hashMiss;
  const label = match
    ? `hash: ${windowHash}`
    : `win=${windowHash} ≠ pat=${patternHash}`;

  ctx.font         = 'bold 10px monospace';
  ctx.textBaseline = 'top';
  const tw    = ctx.measureText(label).width;
  const chipW = tw + 18;
  const chipH = 20;

  ctx.fillStyle = `${color}14`;
  ctx.beginPath();
  ctx.roundRect(x, y, chipW, chipH, chipH / 2);
  ctx.fill();

  ctx.strokeStyle = color;
  ctx.lineWidth   = 1;
  ctx.beginPath();
  ctx.roundRect(x, y, chipW, chipH, chipH / 2);
  ctx.stroke();

  ctx.fillStyle = color;
  ctx.fillText(label, x + 9, y + 5);
}

// ── Payload extraction ─────────────────────────────────────────────────────────

interface StringRow {
  label: string;
  chars: string[];
  xOffset: number;  // horizontal offset for this row
  scanIdx: number;
  matchedSet: Set<number>;
  mismatchSet: Set<number>;
  winStart: number;
  winEnd: number;
}

interface ParsedStringState {
  rows: StringRow[];
  failTable: number[] | null;
  windowHash: number | string | null;
  patternHash: number | string | null;
  explanation: string;
  additionalRows: Array<{
    label: string;
    data: (string | number)[];
    highlights?: Set<number>;
  }>;
}

function parseStep(step: ExecutionStep | null): ParsedStringState | null {
  if (!step) return null;
  const p    = step.payload;
  const meta = (p as { metadata?: StringMetadata }).metadata ?? {};
  const expl = step.metadata?.explanation ?? '';
  
  const rows: StringRow[] = [];
  let text = '', pattern = '', scanIdx = -1;

  if (p.type === 'string') {
    const sp = p as StringStatePayload;
    text    = meta.word1 ?? sp.value ?? '';
    pattern = meta.word2 ?? meta.pattern ?? '';
    scanIdx = meta.currentIndex ?? -1;
  } else if (p.type === 'array') {
    const items = (p as ArrayStatePayload).items ?? [];
    text    = items.map((it) => String(it.value ?? '?')).join('');
    scanIdx = meta.currentIndex ?? -1;
  } else {
    return null;
  }
  if (!text) return null;

  const matchedSet  = new Set<number>(meta.matchedIndices ?? []);
  const mismatchSet = new Set<number>();

  const compareIdx: number = meta.compareIndex ?? -1;
  let winStart = -1;
  let winEnd   = -1;

  if (scanIdx >= 0 && compareIdx >= 0) {
    winStart = Math.min(scanIdx, compareIdx);
    winEnd   = Math.max(scanIdx, compareIdx);
  } else if (meta.windowStart !== undefined && meta.windowEnd !== undefined) {
    winStart = meta.windowStart;
    winEnd   = meta.windowEnd;
  }

  if (scanIdx >= 0 && !matchedSet.has(scanIdx) && compareIdx >= 0 && scanIdx !== compareIdx)
    mismatchSet.add(scanIdx);
  (meta.mismatchIndices as number[] ?? []).forEach((i) => mismatchSet.add(i));

  // Main text row
  rows.push({
    label: 'Text',
    chars: text.split(''),
    xOffset: 0,
    scanIdx,
    matchedSet,
    mismatchSet,
    winStart,
    winEnd,
  });

  // Pattern row (if exists)
  if (pattern) {
    const patChars = pattern.split('');
    const patXOffset = winStart >= 0 ? winStart : 0;
    
    // Build local match/mismatch sets for pattern row
    const patMatchedSet  = new Set<number>();
    const patMismatchSet = new Set<number>();
    patChars.forEach((_ch, j) => {
      const tIdx = patXOffset + j;
      if (matchedSet.has(tIdx))  patMatchedSet.add(j);
      if (mismatchSet.has(tIdx)) patMismatchSet.add(j);
    });

    const patScanLocal =
      scanIdx >= patXOffset && scanIdx < patXOffset + patChars.length
        ? scanIdx - patXOffset : -1;

    rows.push({
      label: 'Pattern',
      chars: patChars,
      xOffset: patXOffset,
      scanIdx: patScanLocal,
      matchedSet: patMatchedSet,
      mismatchSet: patMismatchSet,
      winStart: -1,
      winEnd: -1,
    });
  }

  // Support for additional rows (Z-array, suffix array, etc.) are handled via additionalRows.

  const failTable: number[] | null =
    Array.isArray(meta.failureTable) ? meta.failureTable :
    Array.isArray(meta.lpsTable)     ? meta.lpsTable     : null;

  // Extract additional rows for multi-row algorithms (Aho-Corasick, Z-function, etc.)
  const additionalRows: Array<{
    label: string;
    data: (string | number)[];
    highlights?: Set<number>;
  }> = [];

  if (Array.isArray(meta.zArray)) {
    additionalRows.push({
      label: 'Z',
      data: meta.zArray,
      highlights: new Set(meta.highlightIndices ?? []),
    });
  }

  if (Array.isArray(meta.prefixArray)) {
    additionalRows.push({
      label: 'π',
      data: meta.prefixArray,
      highlights: new Set(meta.highlightIndices ?? []),
    });
  }

  if (Array.isArray(meta.linkArray)) {
    additionalRows.push({
      label: 'Link',
      data: meta.linkArray,
      highlights: new Set(meta.highlightIndices ?? []),
    });
  }

  if (Array.isArray(meta.suffixArray)) {
    additionalRows.push({
      label: 'SA',
      data: meta.suffixArray.map((v) => String(v)),
      highlights: new Set(meta.highlightIndices ?? []),
    });
  }

  if (Array.isArray(meta.automatonStates)) {
    additionalRows.push({
      label: 'AC',
      data: meta.automatonStates.map((v) => String(v)),
      highlights: new Set(meta.highlightIndices ?? []),
    });
  }

  return {
    rows,
    failTable,
    windowHash:  meta.windowHash  ?? meta.currentHash ?? null,
    patternHash: meta.patternHash ?? meta.targetHash  ?? null,
    explanation: expl,
    additionalRows,
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

function drawStringSignature(
  ctx: CanvasRenderingContext2D,
  sig: StringSignature,
  state: ParsedStringState,
  W: number,
  _H: number,
  scale: number,
  originX: number,
): void {
  const { rows, explanation } = state;
  if (rows.length === 0) return;
  const firstRow = rows[0];
  if (!firstRow) return;

  // Helper: convert a character index to its actual x-centre in unscaled coords
  const cellCenterX = (idx: number) => originX + (idx * STRIDE + CELL_W / 2) * scale;
  const cellLeftX   = (idx: number) => originX + idx * STRIDE * scale;

  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  switch (sig) {
    case 'kmp': {
      if (explanation.toLowerCase().includes('fallback') || explanation.toLowerCase().includes('shift')) {
        const shiftMatch = explanation.match(/shift\s*(?:to|by|=)\s*(\d+)/i);
        if (shiftMatch) {
          ctx.fillStyle = '#3b82f6';
          ctx.fillText(`KMP shift → ${shiftMatch[1]}`, W / 2, 22);
        }
      }
      if (state.failTable) {
        ctx.fillStyle = C.kmpLabel;
        ctx.textAlign = 'left';
        ctx.fillText('failure function', PAD_X, 22);
      }
      break;
    }

    case 'boyer-moore': {
      if (firstRow.scanIdx >= 0) {
        const x = cellCenterX(firstRow.scanIdx);
        ctx.strokeStyle = '#f97316';
        ctx.fillStyle = '#f97316';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x + 16, PAD_Y_TOP + 12);
        ctx.lineTo(x - 16, PAD_Y_TOP + 12);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x - 16, PAD_Y_TOP + 12);
        ctx.lineTo(x - 10, PAD_Y_TOP + 8);
        ctx.lineTo(x - 10, PAD_Y_TOP + 16);
        ctx.closePath();
        ctx.fill();
        ctx.fillText('← scan', x, PAD_Y_TOP - 2);
      }
      if (explanation.toLowerCase().includes('bad char')) {
        ctx.fillStyle = '#f97316';
        ctx.textAlign = 'right';
        ctx.fillText('BC heuristic', W - 12, 22);
      }
      if (explanation.toLowerCase().includes('good suffix')) {
        ctx.fillStyle = '#a78bfa';
        ctx.textAlign = 'right';
        ctx.fillText('GS heuristic', W - 12, 22);
      }
      break;
    }

    case 'rabin-karp': {
      if (state.windowHash !== null) {
        ctx.fillStyle = '#22c55e';
        ctx.textAlign = 'left';
        ctx.fillText('rolling hash', PAD_X, 22);
      }
      break;
    }

    case 'z-algo': {
      const zRow = state.additionalRows.find((r) => r.label === 'Z');
      if (zRow) {
        const zVals = zRow.data as number[];
        let maxZ = 0;
        let maxZIdx = 0;
        for (let i = 1; i < zVals.length; i++) {
          if (zVals[i]! > maxZ) {
            maxZ = zVals[i]!;
            maxZIdx = i;
          }
        }
        if (maxZ > 0 && maxZIdx < firstRow.chars.length) {
          const sx = cellLeftX(maxZIdx) - 3;
          const ex = cellLeftX(maxZIdx + maxZ) + CELL_W * scale - GAP + 3;
          const y = PAD_Y_TOP + CELL_H + 4;
          ctx.strokeStyle = '#e879f9';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(sx, y + 4);
          ctx.lineTo(sx, y);
          ctx.lineTo(ex, y);
          ctx.lineTo(ex, y + 4);
          ctx.stroke();
          ctx.fillStyle = '#e879f9';
          ctx.fillText(`Z-box [${maxZIdx}, ${maxZIdx + maxZ - 1}]`, (sx + ex) / 2, y + 12);
        }
      }
      break;
    }

    case 'manacher': {
      if (firstRow.scanIdx >= 0) {
        const x = cellCenterX(firstRow.scanIdx);
        const y = PAD_Y_TOP + CELL_H / 2 + 12;
        ctx.strokeStyle = '#c084fc';
        ctx.fillStyle = '#c084fc';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillText('center', x, y - 14);
        const expandMatch = explanation.match(/expand.*?(\d+)/i);
        if (expandMatch) {
          const radius = parseInt(expandMatch[1]!, 10);
          ctx.beginPath();
          ctx.moveTo(x - radius * STRIDE * scale / 2, y);
          ctx.lineTo(x + radius * STRIDE * scale / 2, y);
          ctx.stroke();
          ctx.fillText(`r=${radius}`, x, y + 14);
        }
      }
      break;
    }

    case 'aho-corasick': {
      ctx.fillStyle = '#06b6d4';
      ctx.textAlign = 'left';
      ctx.fillText('trie traversal', PAD_X, 22);
      if (explanation.toLowerCase().includes('fail')) {
        ctx.fillStyle = '#ef4444';
        ctx.textAlign = 'right';
        ctx.fillText('fail link', W - 12, 22);
      }
      break;
    }

    case 'naive': {
      ctx.fillStyle = '#94a3b8';
      ctx.textAlign = 'left';
      ctx.fillText('brute force', PAD_X, 22);
      break;
    }

    case 'rolling-hash': {
      ctx.fillStyle = '#84cc16';
      ctx.textAlign = 'left';
      ctx.fillText('rolling hash', PAD_X, 22);
      if (firstRow.scanIdx >= 0) {
        const x = cellCenterX(firstRow.scanIdx);
        const w = CELL_W * scale;
        ctx.strokeStyle = '#84cc16';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x - w / 2, PAD_Y_TOP - 4);
        ctx.lineTo(x + w / 2, PAD_Y_TOP - 4);
        ctx.lineTo(x, PAD_Y_TOP + 2);
        ctx.stroke();
      }
      break;
    }

    case 'kasai': {
      ctx.fillStyle = '#f472b6';
      ctx.textAlign = 'left';
      ctx.fillText('LCP array', PAD_X, 22);
      break;
    }

    case 'suffix-array': {
      ctx.fillStyle = '#fb923c';
      ctx.textAlign = 'left';
      ctx.fillText('rank order', PAD_X, 22);
      break;
    }

    case 'suffix-automaton': {
      ctx.fillStyle = '#a78bfa';
      ctx.textAlign = 'left';
      ctx.fillText('suffix links', PAD_X, 22);
      break;
    }

    case 'ukkonen': {
      ctx.fillStyle = '#22d3ee';
      ctx.textAlign = 'left';
      ctx.fillText('active point', PAD_X, 22);
      break;
    }

    default:
      break;
  }

  ctx.restore();
}

export const StringVisualizer: React.FC<StringVisualizerProps> = memo(({ currentStep, algorithmId = '' }) => {
  const canvasRef       = useRef<HTMLCanvasElement>(null);
  const containerRef    = useRef<HTMLDivElement>(null);
  const rafRef          = useRef<number | null>(null);
  const maxCharsSeenRef = useRef<number>(0);

  const draw = useCallback(() => {
    const canvas    = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const state = parseStep(currentStep);

    if (!state) {
      ctx.fillStyle    = C.label;
      ctx.font         = '13px sans-serif';
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No string data for this step.', W / 2, H / 2);
      return;
    }

    const { rows, failTable, windowHash, patternHash, explanation, additionalRows } = state;

    // Reset maxCharsSeenRef on the first step of a new run so the scale
    // is computed from the current input, not a previous (possibly wider) one.
    if (currentStep?.stepId === 0) {
      maxCharsSeenRef.current = 0;
    }

    // Compute adaptive scale — scale down if string doesn't fit.
    // maxCharsSeenRef tracks the widest row ever seen so the scale never
    // grows (zooms in) when a later step has fewer characters.
    const rawMaxChars = rows.reduce((m, r) => Math.max(m, r.chars.length), 0);
    maxCharsSeenRef.current = Math.max(maxCharsSeenRef.current, rawMaxChars);
    const maxChars = maxCharsSeenRef.current;
    const availW = W - 2 * PAD_X;
    const idealRowW = maxChars * STRIDE;
    const scale = idealRowW > availW ? availW / idealRowW : 1;
    const totalRowW = rawMaxChars * STRIDE * scale;
    const centeredX = Math.max(PAD_X, (W - totalRowW) / 2);

    // Section title — centered
    ctx.fillStyle    = '#3f3f46';
    ctx.font         = 'bold 9px monospace';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('STRING  ANALYSIS', W / 2, 10);

    // Hash chip (top-right)
    if (windowHash !== null && patternHash !== null) {
      drawHashChip(ctx, windowHash, patternHash, W - 270, 8);
    }

    // Render all rows dynamically
    let currentY = PAD_Y_TOP + 12;
    const firstRow = rows[0];
    
    // Apply scale transform if cells need to shrink to fit
    if (scale < 1) {
      ctx.save();
      ctx.translate(centeredX, 0);
      ctx.scale(scale, scale);
    }
    const renderX = scale < 1 ? 0 : centeredX;
    
    rows.forEach((row, rowIndex) => {
      const rowX = renderX + row.xOffset * STRIDE;
      
      // Draw vertical connector lines for pattern row (rowIndex 1) if it has a window
      if (rowIndex === 1 && firstRow && firstRow.winStart >= 0) {
        const lx1 = renderX + firstRow.winStart * STRIDE - 4;
        const lx2 = renderX + (firstRow.winEnd + 1) * STRIDE - GAP + 3;
        const ly1 = currentY - ROW_GAP - 2;
        const ly2 = currentY - 6;
        ctx.save();
        ctx.strokeStyle = 'rgba(59,130,246,0.28)';
        ctx.lineWidth   = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(lx1, ly1); ctx.lineTo(lx1, ly2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(lx2, ly1); ctx.lineTo(lx2, ly2); ctx.stroke();
        ctx.restore();
      }

      // Clip region for this row (account for scale transform)
      ctx.save();
      const clipX = rowIndex === 1 && row.xOffset > 0 ? rowX - 62 : renderX;
      const clipWUnscaled = rowIndex === 1 && row.xOffset > 0 ? W - rowX + 54 : W - renderX - 8;
      const clipW = scale < 1 ? clipWUnscaled / scale : clipWUnscaled;
      ctx.beginPath();
      ctx.rect(clipX, currentY - 16, clipW, CELL_H + 32);
      ctx.clip();

      // Draw the character row
      drawCharRow(
        ctx, 
        row.label, 
        row.chars, 
        rowX, 
        currentY, 
        row.scanIdx,
        row.matchedSet, 
        row.mismatchSet, 
        row.winStart, 
        row.winEnd, 
        0
      );
      ctx.restore();

      // Draw KMP failure table below pattern row if applicable
      if (rowIndex === 1 && failTable !== null) {
        const kmpY = currentY + CELL_H + 12;
        drawKmpStrip(ctx, failTable, row.chars, rowX, kmpY);
        currentY += CELL_H + ROW_GAP + 28; // Extra space for KMP table
      } else {
        currentY += CELL_H + ROW_GAP;
      }
    });

    // Restore canvas state after scaled row rendering
    if (scale < 1) {
      ctx.restore();
    }

    // Additional rows for multi-row algorithms (Z-array, prefix function, Aho-Corasick links, etc.)
    if (additionalRows.length > 0) {
      // Apply scale for additional rows too
      if (scale < 1) {
        ctx.save();
        ctx.translate(centeredX, 0);
        ctx.scale(scale, scale);
      }
      const addlRenderX = scale < 1 ? 0 : centeredX;
      additionalRows.forEach((row) => {
        const rowY = currentY;
        const rowData = row.data.map((v) => String(v));
        const highlights = row.highlights ?? new Set<number>();

        // Row label
        ctx.fillStyle    = C.label;
        ctx.font         = 'bold 9px sans-serif';
        ctx.textAlign    = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(row.label.toUpperCase(), addlRenderX - 8, rowY + 11);

        // Draw cells
        rowData.forEach((val, i) => {
          const x = addlRenderX + i * STRIDE;
          const isHighlighted = highlights.has(i);

          ctx.fillStyle = isHighlighted ? C.cellScan : C.kmpCell;
          ctx.beginPath();
          ctx.roundRect(x, rowY, CELL_W, 22, 3);
          ctx.fill();

          ctx.strokeStyle = isHighlighted ? C.borderScan : C.kmpBorder;
          ctx.lineWidth   = isHighlighted ? 2 : 1;
          ctx.beginPath();
          ctx.roundRect(x, rowY, CELL_W, 22, 3);
          ctx.stroke();

          ctx.fillStyle    = isHighlighted ? C.textScan : C.kmpText;
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'middle';
          
          if (val.includes(',')) {
            const lines = val.split(',');
            ctx.font = 'bold 8px monospace';
            ctx.fillText(lines[0] ?? '', x + CELL_W / 2, rowY + 6);
            ctx.fillText(lines[1] ?? '', x + CELL_W / 2, rowY + 16);
          } else {
            ctx.font = 'bold 10px monospace';
            ctx.fillText(val, x + CELL_W / 2, rowY + 11);
          }
        });

        currentY += 28;
      });
      if (scale < 1) {
        ctx.restore();
      }
    }

    // Algorithm-specific signature overlay
    const sig = detectStringSignature(algorithmId);
    if (sig !== 'none') drawStringSignature(ctx, sig, state, W, H, scale, centeredX);

    // Explanation bar
    if (explanation) {
      const barH = 28;
      ctx.fillStyle = C.explainBg;
      ctx.fillRect(0, H - barH, W, barH);
      ctx.fillStyle    = C.explainText;
      ctx.font         = '11px sans-serif';
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(explanation.slice(0, 140), W / 2, H - barH / 2);
    }
  }, [currentStep, algorithmId]);

  // ── ResizeObserver ─────────────────────────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    const canvas    = canvasRef.current;
    if (!container || !canvas) return;
    const sync = () => {
      const { width, height } = container.getBoundingClientRect();
      const w = Math.max(400, Math.round(width));
      const h = Math.max(260, Math.round(height));
      const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
      const styleW = `${w}px`;
      const styleH = `${h}px`;
      if (canvas.style.width !== styleW || canvas.style.height !== styleH || canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.style.width  = styleW;
        canvas.style.height = styleH;
        canvas.width  = w * dpr;
        canvas.height = h * dpr;
      }
      draw();
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(container);
    return () => {
      ro.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [draw]);

  // ── RAF redraw ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    rafRef.current = requestAnimationFrame(() => {
      draw();
      rafRef.current = null;
    });
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [currentStep, draw]);

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0" style={{ display: 'block' }} />
    </div>
  );
});

export default StringVisualizer;
